import type { IconName } from "../lib/brand";

export interface NavItem {
  id: string;
  path: string;
  labelKey: string;
  icon: IconName;
  /** Mocked-only surfaces show a "soon" affordance instead of a full page. */
  stub?: boolean;
  /** Hidden from Free + Pro tenants. Studio+ and operators see it.
   * Used for power-user surfaces like Integrations (BYOK keys) that
   * Vocaler's default reseller flow doesn't expose. */
  studioPlanOnly?: boolean;
}
export interface NavSection {
  labelKey: string;
  items: NavItem[];
  /** When true, the section only renders for operator accounts. */
  operatorOnly?: boolean;
}

// Mirrors the sidebar in project/ui_kits/app/dashboard.jsx.
export const NAV_SECTIONS: NavSection[] = [
  {
    labelKey: "nav.section.studio",
    items: [
      { id: "home", path: "/", labelKey: "nav.home", icon: "home" },
      { id: "generate", path: "/generate", labelKey: "nav.generate", icon: "plus" },
      { id: "library", path: "/library", labelKey: "nav.library", icon: "folders" },
      { id: "renders", path: "/renders", labelKey: "nav.renders", icon: "layers" },
    ],
  },
  {
    labelKey: "nav.section.distribution",
    items: [
      { id: "channels", path: "/channels", labelKey: "nav.channels", icon: "broadcast" },
      { id: "publish", path: "/publish", labelKey: "nav.publish", icon: "send", stub: true },
    ],
  },
  {
    labelKey: "nav.section.developer",
    items: [
      { id: "integrations", path: "/integrations", labelKey: "nav.integrations", icon: "plug", studioPlanOnly: true },
      { id: "cli", path: "/cli", labelKey: "nav.cli", icon: "terminal", stub: true },
      { id: "mcp", path: "/mcp", labelKey: "nav.mcp", icon: "braces", stub: true },
      {
        id: "templates",
        path: "/templates",
        labelKey: "nav.templates",
        icon: "braces",
        stub: true,
      },
    ],
  },
  {
    labelKey: "nav.section.account",
    items: [
      { id: "team", path: "/team", labelKey: "nav.team", icon: "users" },
      { id: "settings", path: "/settings", labelKey: "nav.settings", icon: "settings" },
    ],
  },
];

// Admin — the operator surface, formerly the "Platform" group inside the
// studio sidebar. It is its own shell now: AppShell picks this set for any
// /admin route and the studio set everywhere else, so neither sidebar links
// into the other and the two read as separate products. The OperatorGate on
// each route is the security layer; operatorOnly here is the UX.
export const ADMIN_SECTIONS: NavSection[] = [
  {
    labelKey: "nav.section.admin",
    operatorOnly: true,
    items: [
      { id: "admin-overview", path: "/admin", labelKey: "nav.admin_overview", icon: "layers" },
      { id: "admin-tenants", path: "/admin/tenants", labelKey: "nav.admin_tenants", icon: "folders" },
      { id: "admin-users", path: "/admin/users", labelKey: "nav.admin_users", icon: "users" },
      { id: "admin-jobs", path: "/admin/jobs", labelKey: "nav.admin_jobs", icon: "broadcast" },
      { id: "admin-audit", path: "/admin/audit", labelKey: "nav.admin_audit", icon: "terminal" },
      { id: "admin-settings", path: "/admin/settings", labelKey: "nav.admin_settings", icon: "settings" },
    ],
  },
];

/** True for any route belonging to the admin surface. */
export function isAdminPath(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

// Primary destinations for the mobile bottom tab bar.
export const MOBILE_TABS: NavItem[] = [
  { id: "home", path: "/", labelKey: "nav.home", icon: "home" },
  { id: "generate", path: "/generate", labelKey: "nav.generate", icon: "plus" },
  { id: "library", path: "/library", labelKey: "nav.library", icon: "folders" },
  { id: "renders", path: "/renders", labelKey: "nav.renders", icon: "layers" },
  { id: "settings", path: "/settings", labelKey: "nav.settings", icon: "settings" },
];

// The same bar for the admin surface. Without this, admin on a phone would
// offer tabs straight back into studio.
export const ADMIN_MOBILE_TABS: NavItem[] = [
  { id: "admin-overview", path: "/admin", labelKey: "nav.admin_overview", icon: "layers" },
  { id: "admin-tenants", path: "/admin/tenants", labelKey: "nav.admin_tenants", icon: "folders" },
  { id: "admin-users", path: "/admin/users", labelKey: "nav.admin_users", icon: "users" },
  { id: "admin-jobs", path: "/admin/jobs", labelKey: "nav.admin_jobs", icon: "broadcast" },
  { id: "admin-settings", path: "/admin/settings", labelKey: "nav.admin_settings", icon: "settings" },
];
